<template>
	<div class="p-grid">
		<div class="p-col-12">
			<div class="card">

        <Toolbar class="p-mb-4">
          <template #start>
            <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('Suppliers') }}</div>
            <AddNewButton v-show="canModerate" :label="$t('Add')" @click="addNewItem"/>
          </template>
          <template #end>
            <SearchInput :disabled="dataTableIsLoading" @change-processed-search-value="changeProcessedSearchValue" v-model="searchData" @show-spinner="isLoading = true"/>
          </template>
        </Toolbar>

        <Spinner v-if="isLoading"/>
        <DesktopDataTable v-else
                          :showHeader="false"
                          :dataTableIsLoading="dataTableIsLoading"
                          :itemsArrayIsEmpty="itemsArrayIsEmpty"
                          :sort="sort"
                          :showSearchInput="false"
                          :searchData="searchData"
                          :actionButtonsCount="actionButtonsCount"
                          @sort-items="sortItems"
                          @search-items="searchItems"
                          :headers="[
              {name: 'company_name', title: $t('Company name'), sortable: true},
              {name: 'name', title: $t('Name'), sortable: false},
              {name: 'reg_number', title: $t('Reg. number'), sortable: false},
              {name: 'vat_number', title: $t('VAT number'), sortable: false},
              {name: 'phone_number', title: $t('Phone'), sortable: false},
              {name: 'email', title: $t('Email'), sortable: false},
              {name: 'created_at', title: $t('Created'), sortable: true},
          ]">
          <template v-slot:body>
            <tr v-for="(item, index) of items"
                :key="index"
                :class="{'updated-item': isItemUpdated(item.id)}">
              <td>
                <span v-if="item.company_name">{{ item.company_name }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <span v-if="item.name">{{ item.name }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <span v-if="item.reg_number">{{ item.reg_number }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <span v-if="item.vat_number">{{ item.vat_number }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <span v-if="item.phone_code && item.phone_number">+{{ item.phone_code }} {{ item.phone_number }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <span v-if="item.email" style="white-space: pre-wrap">{{ item.email }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td class="table-date-line">
                {{ formatBy(item.created_at, item.creator) }}
              </td>
              <td v-show="actionButtonsCount">
                <div class="actions">
                  <EditButton v-show="canModerate"
                              :disabled="dataTableIsLoading" @click="editItem(item)"/>
                  <DeleteButton v-show="canModerate"
                                @click="confirmItemDelete(item)"
                                :disabled="dataTableIsLoading"/>
                </div>
              </td>
            </tr>
          </template>
        </DesktopDataTable>

        <OwnPaginator v-show="items && items.length"
                      :rows="pagination.perPage"
                      :currentPage="pagination.currentPage - 1"
                      :totalPages="pagination.pageCount"
                      :totalRecords="pagination.totalCount"
                      :rowsPerPageOptions="[5,10,25,50,100,200]"
                      :pageLinkSize="11"/>

        <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

        <SupplierModal :visible="itemModal"
                       :item="item"
                       @update-items="updateItems"
                       @close="closeItemModal"/>

        <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                            :modalDataIsLoading="modalDataIsLoading"
                            @close="closeConfirmItemDeleteModal"
                            @delete-item="deleteItem"
                            :data="itemToDeleteData"/>
			</div>

		</div>
	</div>
</template>

<script>
import httpClient from '@/services/http.services'
import SupplierModal from '@/pages/data_directory/components/SupplierModal'
import formatMixins from "@/mixins/formatMixins";
import datatableMixins from "@/mixins/datatableMixins";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";

export default {
  mixins: [ showErrorsMixins, formatMixins, datatableMixins ],
  components: { SupplierModal, },
	data() {
		return {
      fieldsExpand: 'creator',
      apiLink: 'supplier',
      deleteItemUrl: 'supplier/delete',
      routePath: '/suppliers',
      sort: {
        column: 'id',
        direction: '-',
      },
		}
	},
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.checkRouteQuery()
    this.getPageData()
	},
  methods: {
    getPageData() {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.getItems()
    },
    async getItems() {
      if (!this.userCanGetData || this.fetchInFlight) return

      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true

      const ctrl = this.setController?.('suppliers')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          signal: ctrl?.signal
        })
        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        if (this._abortors?.suppliers === ctrl) {
          this._abortors.suppliers = null
        }
      }
    },
	},
  computed: {
    canModerate() {
      return this.$store.getters.isAccountantOrHigher
    },
    actionButtonsCount() {
      return this.canModerate ? 2 : 0
    },
    itemToDeleteData() {
      return this.itemToDelete?.company_name ?? null
    },
  }
}
</script>
<style scoped lang="scss">
td {
  height: 44px
}
</style>